import React, { useEffect } from 'react';
import styled from 'styled-components';

import { withModalWindow } from '@ui/modals/ModalWindowMol.tsx';

import AtriusLogoSVG from '../../../../src/images/atrius-logo-standard.svg';

const StyledModalBox = styled.div`
  &.-desktop {
    width: 415px;
  }

  padding: 0 26px 16px;
  color: var(--color-primaryText);
  font-family: ${({ theme }) => theme.fonts.family};
  text-align: center;
`;

const InfoBox = styled.div`
  line-height: 21px;
  font-family: ${({ theme }) => theme.fonts.family};
  ${({ theme }) => theme.fontSize('14px')};
`;

const SectionHeader = styled.h3`
  && {
    font-weight: 600;
    ${({ theme }) => theme.fontSize('16px')};
    line-height: 16px;
    margin: 24px 0 4px;
  }
`;

const ThirdParty = styled.div`
  ${({ theme }) => theme.fontSize('14px')};
  font-weight: 400;
  line-height: 21px;
  margin: 4px 0;
`;

const MapState = styled.div``;

const PolicyUrl = styled.a`
  line-height: 24px;
  ${({ theme }) => theme.fontSize('16px')};
  font-weight: 400;
  color: var(--color-primary);
  text-decoration: none;
`;

const PropertyNameLabel = styled.span`
  font-weight: 600;
`;

const VersionLabel = styled.h2`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${({ theme }) => theme.fontSize('20px')};
  line-height: 26px;

  margin-top: 6px;
  margin-bottom: 16px;

  && {
    font-weight: 600;
  }

  &:focus {
    outline: none;
  }
`;

const VersionNumber = styled.div`
  ${({ theme }) => theme.fontSize('16px')};
`;

const AtriusLogo = styled.div`
  background: url(${AtriusLogoSVG}) no-repeat top center;
  background-size: contain;
  height: 60px;
  max-width: 234px;
  width: 234px;
  margin: 0 auto 12px;
`;

const OvertureAttribution = styled.div`
  ${({ theme }) => theme.fontSize('14px')};
  font-weight: 400;
  line-height: 21px;
  max-width: 340px;
  margin: 4px auto 0;

  a {
    color: var(--color-primary);
    text-decoration: none;
  }
`;

const MapDataCopyright = styled.div`
  ${({ theme }) => theme.fontSize('14px')};
  font-weight: 400;
  line-height: 21px;
  margin-top: 24px;
`;

const identity = x => x;

export const AttributionModalView = withModalWindow(
  ({ engineName, onModalDismiss, properties, setModalDismissAction, versionNumber, privacyPolicyUrl, tosUrl, T }) => {
    useEffect(() => setModalDismissAction(onModalDismiss), []);

    return (
      <StyledModalBox data-testid="AttributionModalView">
        <VersionLabel id="ModalBoxTitle" data-testid="AttributionVersion" tabIndex="-1">
          <AtriusLogo role="img" aria-label={T('attributionModal:Atrius Logo')} />
          <div>{T('attributionModal:About _engineName_', { engineName })}</div>
          <VersionNumber>{T('attributionModal:Version _versionNumber_', { versionNumber })}</VersionNumber>
        </VersionLabel>
        <div>
          <PolicyUrl data-testid="TermsOfService" href={tosUrl} target="_blank">
            {T('attributionModal:Terms Of Service')}
          </PolicyUrl>
        </div>
        <div>
          <PolicyUrl data-testid="PrivacyPolicy" href={privacyPolicyUrl} target="_blank">
            {T('attributionModal:Privacy Policy')}
          </PolicyUrl>
        </div>
        <SectionHeader>{T('attributionModal:Attributions')}</SectionHeader>
        <ThirdParty>
          <div>Copyright (c) 2020, MapLibre contributors</div>
          <div>Contains code from mapbox-gl-js v1.13 and earlier</div>
        </ThirdParty>
        <ThirdParty>
          <div>
            © OpenStreetMap contributors,{' '}
            <a href="https://overturemaps.org/" target="_blank" rel="noreferrer">
              Overture Maps Foundation
            </a>
          </div>
          <OvertureAttribution>
            geoBoundaries, Esri Community Maps Contributors, TomTom, ESA WorldCover, Daylight Map Distribution
          </OvertureAttribution>
        </ThirdParty>
        <MapState>{renderPropertyGroups(properties, T)}</MapState>
        <MapDataCopyright>Map Data ©2015-{new Date().getFullYear().toString().substr(-2)} Atrius</MapDataCopyright>
      </StyledModalBox>
    );
  },
  '#ModalBoxTitle',
  { fullScreenMobile: true },
);

const renderPropertyGroups = (properties, T) => {
  const simpleItems = properties.filter(p => p && p.type === AttributionDataType.SIMPLE);
  const complexItems = properties.filter(p => p && p.type === AttributionDataType.COMPLEX);

  const complexHeaders = {
    'Map Center': T('attributionModal:Map Center'),
    'Current Location': T('attributionModal:Current Location'),
  };

  return (
    <>
      {simpleItems.length > 0 && (
        <>
          <SectionHeader>{T('attributionModal:Map Properties')}</SectionHeader>
          {simpleItems.map(renderPropertyInfo)}
        </>
      )}
      {complexItems.map(property => (
        <div key={property.label}>
          <SectionHeader>{complexHeaders[property.label] || property.label}</SectionHeader>
          {property.value.filter(identity).map(renderPropertyInfo)}
        </div>
      ))}
    </>
  );
};

const renderPropertyInfo = property => (
  <InfoBox key={property.label}>
    <PropertyNameLabel>{property.label}: </PropertyNameLabel>
    {property.value || 'unknown'}
  </InfoBox>
);

export const AttributionDataType = {
  SIMPLE: 1,
  COMPLEX: 2,
};
